import { useState, useEffect, useCallback } from "react";
import { useUser } from "@/context/UserContext";
import { supabase } from "@/lib/supabase";
import type { SalaryApplicationWithEmployee } from "@/types/salary";

interface ToastState {
  message: string;
  type: "success" | "error";
}

const statusLabelMap: Record<string, string> = {
  pending: "審核中",
  approved: "已核准",
  rejected: "已駁回",
};

const statusColorMap: Record<string, string> = {
  pending: "bg-secondary-100 text-secondary-800",
  approved: "bg-primary-100 text-primary-700",
  rejected: "bg-accent-100 text-accent-700",
};

const statusOptions = [
  { value: "all", label: "全部狀態" },
  { value: "pending", label: "審核中" },
  { value: "approved", label: "已核准" },
  { value: "rejected", label: "已駁回" },
];

const periodOptions = [
  { value: "all", label: "全部時間" },
  { value: "7d", label: "近 7 天" },
  { value: "30d", label: "近 30 天" },
  { value: "90d", label: "近 90 天" },
];

export default function SalaryPage() {
  const { isAdmin } = useUser();
  const [applications, setApplications] = useState<SalaryApplicationWithEmployee[]>([]);
  const [filtered, setFiltered] = useState<SalaryApplicationWithEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<ToastState | null>(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [trialOnly, setTrialOnly] = useState(false);

  const [reviewModal, setReviewModal] = useState<{
    open: boolean;
    app: SalaryApplicationWithEmployee | null;
    action: "approve" | "reject";
    reason: string;
  }>({ open: false, app: null, action: "approve", reason: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("salary_applications")
        .select(
          `*, employees:employee_id ( id, name, employee_code, department, position, role_level )`
        )
        .order("created_at", { ascending: false });

      if (err) throw err;
      setApplications((data || []) as SalaryApplicationWithEmployee[]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "載入失敗";
      setError(msg);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let result = [...applications];

    if (statusFilter !== "all") {
      result = result.filter((a) => a.status === statusFilter);
    }

    if (periodFilter !== "all") {
      const now = new Date();
      const days = periodFilter === "7d" ? 7 : periodFilter === "30d" ? 30 : 90;
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      result = result.filter((a) => new Date(a.created_at) >= cutoff);
    }

    if (trialOnly) {
      result = result.filter(
        (a) => a.employees?.role_level === "trial"
      );
    }

    setFiltered(result);
  }, [applications, statusFilter, periodFilter, trialOnly]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const openReview = (
    app: SalaryApplicationWithEmployee,
    action: "approve" | "reject"
  ) => {
    setReviewModal({ open: true, app, action, reason: "" });
  };

  const closeReview = () => {
    setReviewModal({ open: false, app: null, action: "approve", reason: "" });
  };

  const handleReview = async () => {
    const { app, action, reason } = reviewModal;
    if (!app) return;

    const updates: Record<string, unknown> = {
      status: action === "approve" ? "approved" : "rejected",
      reviewed_at: new Date().toISOString(),
    };

    if (action === "reject") {
      if (!reason.trim()) {
        showToast("請輸入駁回原因", "error");
        return;
      }
      updates.reject_reason = reason.trim();
    } else {
      updates.reject_reason = null;
    }

    const { error: err } = await supabase
      .from("salary_applications")
      .update(updates)
      .eq("id", app.id);

    if (err) {
      showToast("操作失敗：" + err.message, "error");
    } else {
      showToast(
        action === "approve" ? "已核准該筆申請" : "已駁回該筆申請",
        "success"
      );
      closeReview();
      fetchData();
    }
  };

  const formatMoney = (n: number) => "NT$ " + n.toLocaleString("zh-TW");

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <i className="ri-lock-line text-foreground-300 text-4xl mb-3" />
          <p className="text-sm text-foreground-500">您沒有權限查看此頁面</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
            toast.type === "error"
              ? "bg-accent-500 text-background-50"
              : "bg-foreground-800 text-background-50"
          }`}
        >
          <i
            className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`}
          />
          {toast.message}
        </div>
      )}

      {/* Review Modal */}
      {reviewModal.open && reviewModal.app && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeReview}
          />
          <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-xl w-full max-w-md p-5 md:p-6">
            <h3 className="text-base font-bold text-foreground-900 mb-1">
              {reviewModal.action === "approve" ? "確認核准" : "確認駁回"}
            </h3>
            <p className="text-sm text-foreground-500 mb-4">
              {reviewModal.app.employees?.name || "未知員工"} ·{" "}
              {formatMoney(reviewModal.app.amount)}
            </p>

            {reviewModal.action === "reject" && (
              <div className="mb-4">
                <label className="block text-xs text-foreground-600 mb-1.5">
                  駁回原因 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  value={reviewModal.reason}
                  onChange={(e) =>
                    setReviewModal((p) => ({ ...p, reason: e.target.value }))
                  }
                  placeholder="請輸入駁回原因，員工將可看見此說明"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50 resize-none"
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeReview}
                className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleReview}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-background-50 hover:opacity-90 transition-colors cursor-pointer whitespace-nowrap ${
                  reviewModal.action === "approve"
                    ? "bg-primary-500"
                    : "bg-accent-500"
                }`}
              >
                {reviewModal.action === "approve" ? "確認核准" : "確認駁回"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1
          className="text-xl font-bold text-foreground-900"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          薪資管理
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          主管/管理員審核員工薪資提領申請
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <p className="text-xs text-foreground-500">
          <i className="ri-information-line mr-1" />
          可按下方篩選僅顯示試用期員工申請。
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-background-200 text-sm text-foreground-700 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {periodOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-background-200 text-sm text-foreground-700 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1.5 rounded-lg">
            共 {filtered.length} 筆
          </span>
        </div>
      </div>

      {/* Toggle trial only */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setTrialOnly(!trialOnly)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
            trialOnly
              ? "bg-primary-100 text-primary-700"
              : "bg-background-100 text-foreground-500 hover:bg-background-200"
          }`}
        >
          <i className="ri-filter-3-line" />
          {trialOnly ? "僅試用期：開啟" : "僅試用期"}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-primary-500 text-3xl" />
            <p className="text-sm text-foreground-500">載入中...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="ri-error-warning-line text-accent-400 text-4xl mb-3" />
          <p className="text-sm text-foreground-500 mb-3">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            重試
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-background-50 border border-background-200 rounded-xl">
          <i className="ri-inbox-line text-foreground-300 text-4xl mb-3" />
          <p className="text-sm text-foreground-400">尚無符合條件的申請紀錄</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((app) => (
            <div
              key={app.id}
              className="bg-background-50 border border-background-200 rounded-xl p-5 hover:border-background-300 transition-colors"
            >
              {/* Top row: ID + status + actions */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold text-foreground-800">
                    {app.id.slice(0, 8)}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColorMap[app.status]}`}
                  >
                    {statusLabelMap[app.status]}
                  </span>
                  {app.employees?.role_level === "trial" && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                      trial
                    </span>
                  )}
                </div>
                {app.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openReview(app, "reject")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-accent-300 text-xs font-medium text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-circle-line" />
                      駁回
                    </button>
                    <button
                      onClick={() => openReview(app, "approve")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-check-line" />
                      核准
                    </button>
                  </div>
                )}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-y-3 gap-x-4 text-sm">
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    員工編號
                  </span>
                  <span className="text-foreground-800 font-mono">
                    {app.employees?.employee_code || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    員工姓名
                  </span>
                  <span className="text-foreground-800">
                    {app.employees?.name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    部門 / 職稱
                  </span>
                  <span className="text-foreground-800">
                    {app.employees?.department || "—"} ·{" "}
                    {app.employees?.position || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    申請時數
                  </span>
                  <span className="text-foreground-800">
                    {app.work_hours || "—"} 小時
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    申請金額
                  </span>
                  <span className="text-foreground-900 font-semibold text-primary-600">
                    {formatMoney(app.amount)}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    銀行名稱
                  </span>
                  <span className="text-foreground-800">
                    {app.bank_name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    銀行帳號
                  </span>
                  <span className="text-foreground-800 font-mono">
                    {app.bank_account_number || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    申請時間
                  </span>
                  <span className="text-foreground-800">
                    {new Date(app.created_at).toLocaleString("zh-TW", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>

              {/* Notes */}
              {app.notes && (
                <div className="mt-3">
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    備註說明
                  </span>
                  <p className="text-sm text-foreground-700 bg-background-100 rounded-md px-3 py-2 inline-block">
                    {app.notes}
                  </p>
                </div>
              )}

              {/* Reject reason */}
              {app.status === "rejected" && app.reject_reason && (
                <div className="mt-3">
                  <span className="text-xs text-foreground-500 block mb-0.5">
                    駁回原因
                  </span>
                  <p className="text-sm text-accent-700 bg-accent-50 rounded-md px-3 py-2 inline-block">
                    {app.reject_reason}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}